import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Badge } from "@/components/ui/badge";
import { Target, Clock, Users, CheckCircle2, AlertCircle } from "lucide-react";
import { cn } from "@/lib/utils";

interface SuccessCriteriaSectionProps {
  successSignal?: string;
  timeframe?: string;
  validators?: string[];
  additionalNotes?: string;
  // Fallback: Array of signal strings from older format
  successSignals?: string[];
}

import { User, Users2, Target as TargetIcon, BarChart3 } from "lucide-react";

type ValidatorInfo = { label: string; Icon: React.ComponentType<{ className?: string }> };

const validatorLabels: Record<string, ValidatorInfo> = {
  pilot: { label: "Pilotkunde", Icon: User },
  team: { label: "Internes Team", Icon: Users2 },
  board: { label: "Entscheider / Board", Icon: TargetIcon },
  data: { label: "Daten / Metrik", Icon: BarChart3 },
  // Also support full label strings
  "Pilotkunde": { label: "Pilotkunde", Icon: User },
  "Internes Team": { label: "Internes Team", Icon: Users2 },
  "Entscheider / Board": { label: "Entscheider / Board", Icon: TargetIcon },
  "Daten / Metrik": { label: "Daten / Metrik", Icon: BarChart3 },
};

export function SuccessCriteriaSection({ 
  successSignal, 
  timeframe, 
  validators = [],
  additionalNotes,
  successSignals = []
}: SuccessCriteriaSectionProps) {
  // If we have structured data, use it; otherwise fall back to simple signals
  const hasStructuredData = successSignal || timeframe || validators.length > 0;
  
  // If no data at all, don't render
  if (!hasStructuredData && successSignals.length === 0) {
    return null;
  }

  // Parse validators if they're strings
  const parsedValidators = validators.map(v => {
    const info = validatorLabels[v];
    return info || { label: v, Icon: CheckCircle2 };
  });

  return (
    <Card 
      id="success-criteria" 
      className="scroll-mt-24 border-2 border-emerald-500/30 bg-gradient-to-br from-emerald-500/5 to-transparent"
    >
      <CardHeader className="pb-4">
        <div className="flex items-center justify-between">
          <CardTitle className="text-lg flex items-center gap-2">
            <Target className="h-5 w-5 text-emerald-500" />
            Erfolgskriterien
          </CardTitle>
          <Badge variant="outline" className="text-xs bg-emerald-500/10 border-emerald-500/30 text-emerald-700 dark:text-emerald-400">
            Phase 1.1 Definition
          </Badge>
        </div>
        <p className="text-xs text-muted-foreground mt-1">
          Diese Kriterien definieren, woran der Erfolg dieser Entscheidung gemessen wird
        </p>
      </CardHeader>
      
      <CardContent className="space-y-6">
        {/* Structured view if we have the data */}
        {hasStructuredData ? (
          <>
            {/* Signal - Das Hauptkriterium */}
            {successSignal && (
              <div className="space-y-2">
                <div className="flex items-center gap-2">
                  <CheckCircle2 className="h-4 w-4 text-emerald-500" />
                  <h4 className="font-semibold text-sm">Beobachtbares Erfolgssignal</h4>
                </div>
                <div className="ml-6 p-4 bg-emerald-500/5 border border-emerald-500/20 rounded-lg">
                  <p className="text-base font-medium text-foreground leading-relaxed">
                    "{successSignal}"
                  </p>
                </div>
              </div>
            )}

            {/* Timeframe and Validators Grid */}
            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
              {/* Zeitrahmen */}
              {timeframe && (
                <div className="p-4 bg-muted/30 rounded-lg border border-border/50">
                  <div className="flex items-center gap-2 mb-2">
                    <Clock className="h-4 w-4 text-blue-500" />
                    <h4 className="font-semibold text-sm">Zeitrahmen</h4>
                  </div>
                  <p className="text-lg font-bold text-blue-600 dark:text-blue-400">
                    {timeframe}
                  </p>
                  <p className="text-xs text-muted-foreground mt-1">
                    Bis wann das Signal eingetreten sein muss
                  </p>
                </div>
              )}

              {/* Validatoren */}
              {parsedValidators.length > 0 && (
                <div className="p-4 bg-muted/30 rounded-lg border border-border/50">
                  <div className="flex items-center gap-2 mb-2">
                    <Users className="h-4 w-4 text-violet-500" />
                    <h4 className="font-semibold text-sm">Validierung durch</h4>
                  </div>
                  <div className="flex flex-wrap gap-2">
                    {parsedValidators.map((validator, index) => {
                      const ValidatorIcon = validator.Icon;
                      return (
                        <Badge 
                          key={index}
                          variant="secondary"
                          className="bg-violet-500/10 border-violet-500/30 text-violet-700 dark:text-violet-400"
                        >
                          <ValidatorIcon className="h-3 w-3 mr-1" />
                          {validator.label}
                        </Badge>
                      );
                    })}
                  </div>
                  <p className="text-xs text-muted-foreground mt-2">
                    Wer bestätigt, dass das Signal relevant ist
                  </p>
                </div>
              )}
            </div>

            {/* Additional notes if present */}
            {additionalNotes && (
              <div className="pt-4 border-t border-border/50">
                <div className="flex items-center gap-2 mb-2">
                  <AlertCircle className="h-4 w-4 text-muted-foreground" />
                  <h4 className="font-medium text-sm text-muted-foreground">Zusätzliche Hinweise</h4>
                </div>
                <p className="text-sm text-muted-foreground ml-6 italic">
                  {additionalNotes}
                </p>
              </div>
            )}
          </>
        ) : (
          /* Fallback: Simple list of signals (legacy format) */
          <div className="space-y-2">
            <div className="flex items-center gap-2 mb-3">
              <CheckCircle2 className="h-4 w-4 text-emerald-500" />
              <h4 className="font-semibold text-sm">Definierte Erfolgssignale</h4>
            </div>
            <ul className="space-y-2 ml-6">
              {successSignals.slice(0, 4).map((signal, index) => (
                <li 
                  key={index} 
                  className={cn(
                    "flex items-start gap-3 p-3 rounded-lg border",
                    index === 0 
                      ? "bg-emerald-500/5 border-emerald-500/20" 
                      : "bg-muted/20 border-border/50"
                  )}
                >
                  <span className="text-emerald-500 font-bold shrink-0 mt-0.5">
                    {index + 1}.
                  </span>
                  <span className="text-sm leading-relaxed">{signal}</span>
                </li>
              ))}
            </ul>
          </div>
        )}

        {/* Commitment Reminder */}
        <div className="mt-4 p-3 bg-amber-500/5 border border-amber-500/20 rounded-lg">
          <p className="text-xs text-amber-700 dark:text-amber-400 flex items-center gap-2">
            <AlertCircle className="h-3.5 w-3.5" />
            <span>
              Diese Kriterien sind die Grundlage für die Review-Entscheidung am Ende des festgelegten Zeitraums.
            </span>
          </p>
        </div>
      </CardContent>
    </Card>
  );
}
